'use client';

import { useState } from 'react';

import { StarShape } from '@eliadmin/core/src/components/dlz/ui/rating-stars';
import { REVIEW_COPY } from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-copy';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type ReviewRatingInputProps = {
  /** 0 = nothing chosen yet. */
  value: number;
  onChange: (value: number) => void;
  invalid?: boolean;
  /** Ids of the hint / error text that describe the group. */
  describedBy?: string;
  /** Accessible name of the group (the visible label sits next to it in the form). */
  label: string;
};

/**
 * Figma „Rating input“ (60152:14539): five 32 px stars and the word for the chosen rating. Native
 * radio buttons, so the arrow keys, a single tab stop and the form semantics come for free.
 */
export function ReviewRatingInput({
  value,
  onChange,
  invalid,
  describedBy,
  label,
}: ReviewRatingInputProps) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;

  return (
    <div className="flex items-center gap-4">
      <div
        role="radiogroup"
        aria-label={label}
        aria-required="true"
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className="flex gap-0.5"
        onMouseLeave={() => setHover(0)}
      >
        {REVIEW_COPY.form.ratingLabels.map((name, index) => {
          const star = index + 1;
          return (
            <label
              key={star}
              onMouseEnter={() => setHover(star)}
              className="relative flex cursor-pointer rounded-lg has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#0F1012]"
            >
              <input
                type="radio"
                name="rating"
                value={star}
                checked={value === star}
                onChange={() => onChange(star)}
                aria-label={`${star} – ${name}`}
                className="sr-only"
              />
              <StarShape
                size="lg"
                className={cn(
                  'transition-colors',
                  star <= shown ? 'text-[#E89A0C]' : 'text-[#E2E4E7]',
                )}
              />
            </label>
          );
        })}
      </div>
      {shown ? (
        <span aria-hidden="true" className="dlz-body-m-medium text-[#0F1012]">
          {REVIEW_COPY.form.ratingLabels[shown - 1]}
        </span>
      ) : null}
    </div>
  );
}
